import React, {useCallback, useEffect, useRef, useState} from 'react';
import useApi from '../../../hooks/useApi';
import ResponseErrorDialog from '../../../scenes/Dialog/components/ResponseErrorDialog';
import TextInput from '../../UI/TextInput';
import CardRadio from '../CardRadio';
import './styles.css';

const AsyncCardsSelect = ({
  selected,
  onChange,
  options,
  source,
  creatable,
  createNewTitle,
  createNewSubtitle,
  createNewHelp,
  createNewPlaceholder,
  notFoundText,
  searchPlaceholder,
  searchHelp,
  multiple,
  groupSelect,
  showIcon,
  onCreateNewPressEnter,
  autoselect,
  disclaimer,
  columns,
  beforeList,
}) => {
  const [loading, setLoading] = useState(!options);
  const [selectOptions, setSelectOptions] = useState([]);
  const searchTimeout = useRef(null);
  const api = useApi();

  const loadOptions = useCallback((search = '') => {
    setLoading(true);

    if (!source) {
      return;
    }

    api.getRequest(source, { s: search })
      .then((response) => {
        if (response.status !== 'OK') {
          throw response;
        }

        if (response.data?.[0]?.options) {
          setSelectOptions(response.data.map(item => {
            return {
              ...item,
              options: item.options.map(option => ({
                ...option,
                icon: option.logo,
              })),
            };
          }));
        } else {
          setSelectOptions([{
            name: '',
            options: response.data
          }]);
        }

      })
      .catch((error) => {
        ResponseErrorDialog({ response: error });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [api, source]);

  const onInputChange = useCallback(e => {
    onChange({name: e.target.value}, true);
  }, [onChange]);

  const onRadioChange = useCallback(v => {
    onChange(v);
  }, [onChange]);

  const onSearch = useCallback((search) => {
    setLoading(true);
    clearTimeout(searchTimeout.current);

    searchTimeout.current = setTimeout(() => {
      loadOptions(search);
    }, 300);
  }, [loadOptions]);

  const onCreateNewKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && typeof onCreateNewPressEnter === 'function') {
      onCreateNewPressEnter();
    }
  }, [onCreateNewPressEnter]);

  useEffect(() => {
    if (!options) {
      loadOptions();
    } else {
      setSelectOptions(options);
    }
  }, [loadOptions, options]);

  return (
    <div className={'cards-select'}>
      <CardRadio
        loading={loading}
        options={selectOptions}
        selected={selected}
        onChange={onRadioChange}
        notFoundText={notFoundText}
        searchPlaceholder={searchPlaceholder}
        searchHelp={searchHelp}
        onSearch={!options ? onSearch : null}
        multiple={multiple}
        groupSelect={groupSelect}
        showIcon={showIcon}
        disclaimer={disclaimer}
        columns={columns}
        beforeList={beforeList}
      />
      {!multiple && creatable && (
        <div className={'cards-select__custom'}>
          {createNewTitle && (
            <h3>{createNewTitle}</h3>
          )}
          {createNewSubtitle && (
            <p>{createNewSubtitle}</p>
          )}
          <TextInput
            value={selected && !selected.id && selected.name ? selected.name : ''}
            onChange={onInputChange}
            onKeyPress={onCreateNewKeyPress}
            help={createNewHelp}
            showHelpTooltip={false}
            inlineButton={autoselect ? 'Add' : null}
            onButtonClick={() => autoselect && typeof onCreateNewPressEnter === 'function' && onCreateNewPressEnter()}
            placeholder={createNewPlaceholder}/>
        </div>
      )}
    </div>
  );
}

export default AsyncCardsSelect;
